/**
 * Pure helpers behind "Upload data" (TRA-1343), kept out of the component so
 * they are testable without a DOM. The api re-checks every rule here and
 * every row of both files; these catch what the browser can see first.
 *
 * A set too large for one upload is registered from the bucket instead
 * (TRA-1559): staff copy both files into the team's staging folder, or where
 * the stack signs uploads the browser sends them there (`sendToStaging`),
 * and the api checks them where they are.
 */

import { z } from 'zod'

import type {
  TrainingSetRegistration,
  TrainingSetStagingOption,
  TrainingSetStagingUpload,
  TrainingSetUploadRequest,
} from '@/api/generated/model'

/** The api's SET_ID_PATTERN, the keeper publisher's own rule. */
const SET_ID = /^[A-Za-z0-9][A-Za-z0-9._-]{0,127}$/

/** services/training_sets.MAX_UPLOAD_BYTES: both files together. */
export const MAX_UPLOAD_BYTES = 45 * 1024 * 1024

export const uploadSetSchema = z
  .object({
    setId: z
      .string()
      .regex(
        SET_ID,
        'A data name starts with a letter or digit and holds only letters, digits, dots, hyphens or underscores.',
      ),
    /** The api's SetLicense: the terms the data may be trained under, never blank. */
    license: z.string().trim().min(1, 'Name the terms the data may be trained under.'),
    train: z.instanceof(File, { message: 'Pick the training file.' }),
    validation: z.instanceof(File, { message: 'Pick the validation file.' }),
  })
  .refine((form) => form.train.size + form.validation.size <= MAX_UPLOAD_BYTES, {
    message: `One upload takes up to ${String(MAX_UPLOAD_BYTES / 2 ** 20)} MB. These files are larger together.`,
    path: ['validation'],
  })

export type UploadSetForm = z.infer<typeof uploadSetSchema>

export type UploadSetField = 'setId' | 'license' | 'train' | 'validation'

/** The form as the inputs hold it, before validation. */
export interface UploadSetDraft {
  setId: string
  license: string
  train: File | null
  validation: File | null
}

export type UploadSetErrors = Partial<Record<UploadSetField, string>>

/** The checked form, or each field's first problem. */
export type ParsedUploadSet =
  | { kind: 'ok'; form: UploadSetForm }
  | { kind: 'error'; errors: UploadSetErrors }

export function parseUploadSet(draft: UploadSetDraft): ParsedUploadSet {
  const result = uploadSetSchema.safeParse(draft)
  if (result.success) return { kind: 'ok', form: result.data }
  const errors: UploadSetErrors = {}
  for (const issue of result.error.issues) {
    const field = issue.path[0]
    if (
      (field === 'setId' || field === 'license' || field === 'train' || field === 'validation') &&
      !(field in errors)
    ) {
      errors[field] = issue.message
    }
  }
  return { kind: 'error', errors }
}

/** The request, or why these files cannot be sent. */
export type PreparedUpload =
  | { kind: 'ok'; request: TrainingSetUploadRequest }
  | { kind: 'error'; message: string }

/**
 * A file's exact text, or null when it is not valid UTF-8. `File.text()`
 * would swap bad bytes for U+FFFD, and the api would store content the file
 * never held. A byte order mark is kept, so the stored bytes are the file's.
 */
async function strictText(file: File): Promise<string | null> {
  try {
    return new TextDecoder('utf-8', { fatal: true, ignoreBOM: true }).decode(
      await file.arrayBuffer(),
    )
  } catch {
    return null
  }
}

/** The request body: both files' text, as read, as an SFT chat set under the license named. */
export async function prepareUpload(
  form: UploadSetForm,
  limit: number = MAX_UPLOAD_BYTES,
): Promise<PreparedUpload> {
  const [train, validation] = await Promise.all([
    strictText(form.train),
    strictText(form.validation),
  ])
  if (train === null) return { kind: 'error', message: 'The training file is not valid UTF-8 text.' }
  if (validation === null) {
    return { kind: 'error', message: 'The validation file is not valid UTF-8 text.' }
  }
  const request: TrainingSetUploadRequest = {
    set_id: form.setId,
    recipe: 'sft-chat',
    license: form.license,
    train: { name: form.train.name, content: train },
    validation: { name: form.validation.name, content: validation },
  }
  // JSON escapes quotes and backslashes, so the body can outgrow the files.
  // The ingress and the api both measure what is sent.
  if (new TextEncoder().encode(JSON.stringify(request)).length > limit) {
    return {
      kind: 'error',
      message: `One upload takes up to ${String(limit / 2 ** 20)} MB. These files are larger than that as sent.`,
    }
  }
  return { kind: 'ok', request }
}


// ------------------------------------------- sets from the bucket (TRA-1559)

/** The two files a set's staging folder holds, by the names the api reads. */
export const STAGED_FILES = ['train.jsonl', 'validation.jsonl'] as const

/** The set's staging folder: gs://<data bucket>/training-demonstrations/<team>/staging/<set id>/. */
export function stagingFolder(staging: TrainingSetStagingOption, setId: string): string {
  return `gs://${staging.bucket}/${staging.prefix}${setId}/`
}

/** What staff run inside the perimeter to copy both files there. */
export function gcloudCommand(staging: TrainingSetStagingOption, setId: string): string {
  return `gcloud storage cp train.jsonl validation.jsonl ${stagingFolder(staging, setId)}`
}

export const registerSetSchema = z.object({
  setId: uploadSetSchema.innerType().shape.setId,
  license: uploadSetSchema.innerType().shape.license,
})

export type RegisterSetForm = z.infer<typeof registerSetSchema>

/** The checked registration, or each field's first problem. */
export type ParsedRegisterSet =
  | { kind: 'ok'; form: RegisterSetForm }
  | { kind: 'error'; errors: UploadSetErrors }

export function parseRegisterSet(draft: { setId: string; license: string }): ParsedRegisterSet {
  const result = registerSetSchema.safeParse(draft)
  if (result.success) return { kind: 'ok', form: result.data }
  const errors: UploadSetErrors = {}
  for (const issue of result.error.issues) {
    const field = issue.path[0]
    if ((field === 'setId' || field === 'license') && !(field in errors)) errors[field] = issue.message
  }
  return { kind: 'error', errors }
}

function megabytes(bytes: number): string {
  return (bytes / 2 ** 20).toLocaleString(undefined, { maximumFractionDigits: 0 })
}

/** How a registration stands, for the line under the form. */
export function registrationLine(status: TrainingSetRegistration): string {
  if (status.state === 'registered') return `Data ${status.set_id} is ready to train.`
  if (status.state === 'refused') return status.detail ?? `Data ${status.set_id} was refused.`
  return `The check has read ${megabytes(status.checked_bytes)} of ${megabytes(status.total_bytes)} MB of both files.`
}

/** The answer of the bucket a signed upload talks to, as sendToStaging reads it. */
export interface StagingTransport {
  /** POST the signed URL with its headers; the session URL from the Location header. */
  start: (url: string, headers: Record<string, string>) => Promise<string>
  /** PUT the file's bytes to the session URL, reporting the bytes sent. */
  put: (session: string, file: File, onProgress: (sent: number) => void) => Promise<void>
}

/**
 * One file straight into the staging folder: the signed URL starts a
 * resumable upload, and the file goes to the session URL the bucket answers
 * with in one PUT. The bucket's CORS rule must let this origin POST, PUT and
 * read the Location header (charts/platform/README.md).
 */
export async function sendToStaging(
  signed: TrainingSetStagingUpload,
  file: File,
  onProgress: (sent: number) => void,
  transport: StagingTransport = browserTransport,
): Promise<void> {
  const session = await transport.start(signed.url, signed.headers)
  await transport.put(session, file, onProgress)
}

/** fetch for the start and XMLHttpRequest for the PUT, the one that reports upload progress. */
export const browserTransport: StagingTransport = {
  start: async (url, headers) => {
    const response = await fetch(url, { method: 'POST', headers })
    const session = response.headers.get('Location')
    if (!response.ok || session === null) {
      throw new Error(`The bucket did not start the upload (HTTP ${String(response.status)}). Check its CORS rule.`)
    }
    return session
  },
  put: (session, file, onProgress) =>
    new Promise<void>((resolve, reject) => {
      const request = new XMLHttpRequest()
      request.open('PUT', session)
      request.upload.onprogress = (event) => {
        onProgress(event.loaded)
      }
      request.onload = () => {
        if (request.status >= 200 && request.status < 300) resolve()
        else reject(new Error(`The bucket refused the file (HTTP ${String(request.status)}).`))
      }
      request.onerror = () => {
        reject(new Error('The upload to the bucket failed. The browser may not reach storage.googleapis.com.'))
      }
      request.send(file)
    }),
}
